import {
  getConsentFromStorage,
  has,
  type AllConsentNames,
  type ConsentState,
} from 'c15t';

/**
 * Read-only consent enforcement for the legacy SPA.
 *
 * belong-web mounts no banner and no consent provider. It does not need to:
 * `next.config.js` proxies every unmatched path here through a server-side
 * `fallback` rewrite, so the browser only ever sees `belonghome.com`. The `c15t`
 * cookie written by the marketing pages is therefore same-origin and directly
 * readable, and `getConsentFromStorage` prioritises the cookie over
 * localStorage precisely so this kind of cross-app read works.
 *
 * The practical consequence: a visitor reaches an account page only after
 * passing through a marketing page, so a choice already exists. When it does
 * not — a direct link, a cleared cookie — we deny everything non-essential and
 * let the footer link on the marketing side be the way back in. Failing closed
 * is the whole point.
 */
type StoredConsent = {
  consents?: Partial<ConsentState>;
};

/**
 * Every category defaults to denied. `getConsentFromStorage` normalises stored
 * values to explicit booleans, but a *missing* record has to be treated as a
 * refusal rather than as an absence of information.
 */
const DENY_ALL: ConsentState = {
  necessary: true,
  functionality: false,
  experience: false,
  measurement: false,
  marketing: false,
};

function readConsentState(): ConsentState {
  if (typeof window === 'undefined') {
    return DENY_ALL;
  }

  try {
    const stored = getConsentFromStorage<StoredConsent>();

    if (!stored?.consents) {
      return DENY_ALL;
    }

    return { ...DENY_ALL, ...stored.consents };
  } catch {
    // Private-mode storage errors, a malformed cookie — anything unreadable
    // means we have no consent on record.
    return DENY_ALL;
  }
}

/**
 * Whether the visitor has granted the given category.
 *
 * Read at call time rather than cached: the consent cookie can change in
 * another tab on the marketing side while this SPA is open, and this app has no
 * subscription to that.
 */
export function hasConsent(category: AllConsentNames): boolean {
  return has(category, readConsentState());
}

/**
 * c15t category -> Google Consent Mode v2 signal mapping.
 *
 * Mirrors the mapping passed to c15t's Google helpers in
 * `apps/belong-next/src/features/consent/constants.ts`. The two must agree: a
 * visitor moving from a marketing page into the SPA has to see the same consent
 * state applied to the same Google container.
 */
const CONSENT_MODE_MAPPING: Record<AllConsentNames, string[]> = {
  necessary: ['security_storage'],
  functionality: ['functionality_storage'],
  experience: ['personalization_storage'],
  measurement: ['analytics_storage'],
  marketing: ['ad_storage', 'ad_user_data', 'ad_personalization'],
};

/**
 * Pushes Google Consent Mode v2 defaults derived from the stored consent.
 *
 * Must run before the GTM container loads (`helpers/GoogleTagManager.js`
 * injects it from an effect, so module-scope in `index.js` is early enough).
 *
 * Unlike belong-next, this pushes the visitor's *actual* stored state rather
 * than a blanket `denied` plus a later `update`: there is no banner here, so
 * nothing will ever arrive to update it. A visitor with no stored record gets
 * everything denied.
 */
export function applyStoredConsentModeDefaults(): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  const state = readConsentState();
  const signals: Record<string, 'granted' | 'denied'> = {};

  for (const [category, googleSignals] of Object.entries(
    CONSENT_MODE_MAPPING,
  )) {
    const granted = state[category as AllConsentNames] === true;

    for (const signal of googleSignals) {
      signals[signal] = granted ? 'granted' : 'denied';
    }
  }

  window.gtag('consent', 'default', signals);
}
